import { FieldValue } from 'firebase/firestore';
import isEqual from 'lodash.isequal';
import type { ActivityLogChange } from './types';

// Field names that should NEVER be reported as changes regardless of value.
// See `docs/superpowers/specs/2026-05-11-activity-log-design.md` (diffFields
// semantics) for the rationale per key.
export const DIFF_EXCLUDE_KEYS: ReadonlySet<string> = new Set<string>([
  's3LabelUrl',
  'locally_edited_at',
  'ref',
  'eventRef',
  'is_deleted',
]);

// Best-effort detection for Firestore FieldValue sentinels. The SDK doesn't
// expose a public type guard, but `FieldValue` instances all share the marker
// method `_methodName`. We additionally check the constructor name for
// deleteField() / serverTimestamp() / arrayUnion / etc. so we can decide what
// to do in the diff.
type SentinelKind =
  | 'deleteField'
  | 'serverTimestamp'
  | 'arrayUnion'
  | 'arrayRemove'
  | 'increment'
  | 'other';

const isFieldValue = (v: unknown): v is FieldValue =>
  v instanceof FieldValue ||
  (typeof v === 'object' &&
    v !== null &&
    '_methodName' in (v as Record<string, unknown>));

// Resolves a dotted path (e.g. `customFields.<ref>`) against `obj`. Returns
// undefined if any segment along the path is missing or non-object. Lets
// callers pass dotted-key payloads (Firestore per-key merge) and still get
// correct audit diffs — a flat lookup would always miss and report
// `from: null`.
const resolveDottedPath = (obj: unknown, path: string): unknown => {
  if (!path.includes('.')) return undefined;
  const segments = path.split('.');
  let curr: unknown = obj;
  for (const seg of segments) {
    if (curr === null || curr === undefined) return undefined;
    if (typeof curr !== 'object') return undefined;
    curr = (curr as Record<string, unknown>)[seg];
  }
  return curr;
};

const sentinelKind = (v: FieldValue): SentinelKind => {
  // _methodName is a stable string literal on every FieldValue subclass and is
  // safe across CRA's prod minification. The constructor-name fallback is
  // defensive only — it won't survive class-name mangling in `yarn build`, so
  // never rely on it; if `_methodName` ever disappears we'd need a real fix.
  const m =
    (v as { _methodName?: string; constructor?: { name?: string } })
      ._methodName ??
    (v as { constructor?: { name?: string } }).constructor?.name ??
    '';
  if (m.includes('deleteField') || m.includes('Delete')) return 'deleteField';
  if (m.includes('serverTimestamp') || m.includes('ServerTimestamp'))
    return 'serverTimestamp';
  if (m.includes('arrayUnion') || m.includes('ArrayUnion')) return 'arrayUnion';
  if (m.includes('arrayRemove') || m.includes('ArrayRemove'))
    return 'arrayRemove';
  if (m.includes('increment') || m.includes('Increment')) return 'increment';
  return 'other';
};

// Compares the existing Firestore doc (`before`) to the Firestore write
// payload (`afterPayload`) and returns a per-field { from, to } map of just
// the fields that meaningfully changed. See the spec for the full set of
// rules; the summary:
//   - excluded keys are skipped outright
//   - deleteField() sentinel is treated as `to: null`
//   - serverTimestamp() sentinel is skipped (resolved value is unknown
//     client-side, and the only field this targets is on the exclude list)
//   - other FieldValue sentinels are warned and skipped
//   - Timestamps compare by toMillis()
//   - arrays/objects compare deeply
//   - if nothing meaningful changed, returns {} — callers should skip
//     logging the entry entirely on an empty result
export const diffFields = (
  before: object | null | undefined,
  afterPayload: Record<string, unknown>,
): Record<string, ActivityLogChange> => {
  const changes: Record<string, ActivityLogChange> = {};
  const beforeObj = before ?? {};

  for (const key of Object.keys(afterPayload)) {
    if (DIFF_EXCLUDE_KEYS.has(key)) continue;

    let beforeVal: unknown = (beforeObj as Record<string, unknown>)[key];
    if (beforeVal === undefined && key.includes('.')) {
      beforeVal = resolveDottedPath(beforeObj, key);
    }
    const afterVal = afterPayload[key];

    if (isFieldValue(afterVal)) {
      const kind = sentinelKind(afterVal);
      if (kind === 'deleteField') {
        if (beforeVal === undefined || beforeVal === null) continue;
        changes[key] = { from: beforeVal, to: null };
        continue;
      }
      if (kind === 'serverTimestamp') {
        // Skip — resolved value is unknown at write time and the only field
        // we stamp this way (locally_edited_at) is on the exclude list.
        continue;
      }
      // arrayUnion / arrayRemove / increment / other — diff helper isn't the
      // right tool for these. Semantic helpers cover the array-mutation case.
      // Warn and skip so a future code path failure is loud.
      console.warn(
        `diffFields: unhandled FieldValue sentinel "${kind}" on field "${key}" — skipping`,
      );
      continue;
    }

    // `lodash.isequal` handles primitives, arrays, plain objects, and class
    // instances structurally. Firestore `Timestamp` exposes its own
    // enumerable `seconds` + `nanoseconds`, so two equal timestamps compare
    // equal via structural deep-equal without us having to special-case them.
    if (isEqual(beforeVal, afterVal)) continue;

    if (afterVal === undefined) {
      console.warn(
        `diffFields: undefined value for key "${key}" in afterPayload — likely a caller bug (use deleteField() to clear). Skipping.`,
      );
      continue;
    }

    changes[key] = {
      from: beforeVal === undefined ? null : beforeVal,
      to: afterVal,
    };
  }

  return changes;
};
